import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import axios from 'axios';

const HeaderContainer = styled.header`
  background-color: var(--white);
  color: var(--gray-800);
  box-shadow: var(--box-shadow-md);
  position: sticky;
  top: 0;
  z-index: 1000;
`;

const Navbar = styled.nav`
  display: flex;
  justify-content: space-between;
  align-items: center;
  max-width: 1320px;
  margin: 0 auto;
  padding: 0 1rem;
  height: 70px;
`;

const Logo = styled(Link)`
  display: flex;
  align-items: center;
  text-decoration: none;
  
  img {
    height: 55px;
    width: auto;
    transition: all var(--transition-fast) ease-in-out;
  }
  
  &:hover img {
    opacity: 0.8;
  }
`;

const NavLinks = styled.div`
  display: flex;
  gap: 0.5rem;
  
  @media (max-width: 768px) {
    display: none;
  }
`;

const NavLink = styled(Link)`
  color: var(--gray-700);
  text-decoration: none;
  padding: 0.5rem 1rem;
  border-radius: var(--border-radius-md);
  font-weight: 500;
  transition: all var(--transition-fast) ease-in-out;
  position: relative;
  
  &:hover {
    color: var(--primary-color);
    background-color: var(--gray-100);
  }
  
  &.active {
    color: var(--primary-color);
    background-color: var(--gray-100);
  }
`;

// 不带链接的导航项（用于只显示下拉菜单）
const NavItem = styled.div`
  color: var(--gray-700);
  padding: 0.5rem 1rem;
  border-radius: var(--border-radius-md);
  font-weight: 500;
  transition: all var(--transition-fast) ease-in-out;
  position: relative;
  cursor: pointer;
  
  &:hover {
    color: var(--primary-color);
    background-color: var(--gray-100);
  }
  
  &.active {
    color: var(--primary-color);
    background-color: var(--gray-100);
  }
`;

const Dropdown = styled.div`
  position: absolute;
  top: 100%;
  left: 0;
  background-color: var(--white);
  box-shadow: var(--box-shadow-lg);
  border-radius: var(--border-radius-md);
  padding: 0.5rem 0;
  min-width: 200px;
  z-index: 1001;
  display: ${props => props.isOpen ? 'block' : 'none'};
`;

const SolutionsDropdown = styled(Dropdown)`
  min-width: 200px;
  padding: 0.5rem 0;
`;

const DropdownItem = styled(Link)`
  display: block;
  color: var(--gray-700);
  text-decoration: none;
  padding: 0.75rem 1.5rem;
  transition: all var(--transition-fast) ease-in-out;
  font-weight: 500;
  
  &:hover {
    color: var(--primary-color);
    background-color: var(--gray-100);
  }
`;

const AuthLinks = styled.div`
  display: flex;
  gap: 0.5rem;
  align-items: center;
  
  @media (max-width: 768px) {
    display: none;
  }
`;

const LoginButton = styled(Link)`
  color: var(--gray-700);
  text-decoration: none;
  padding: 0.5rem 1rem;
  border-radius: var(--border-radius-md);
  font-weight: 500;
  transition: all var(--transition-fast) ease-in-out;
  
  &:hover {
    color: var(--primary-color);
    background-color: var(--gray-100);
  }
`;

const SignupButton = styled(Link)`
  color: var(--white);
  background-color: var(--primary-color);
  text-decoration: none;
  padding: 0.5rem 1rem;
  border-radius: var(--border-radius-md);
  font-weight: 500;
  transition: all var(--transition-fast) ease-in-out;
  
  &:hover {
    background-color: var(--primary-dark);
    transform: translateY(-1px);
    box-shadow: var(--box-shadow-sm);
  }
`;

const UserMenu = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  cursor: pointer;
  padding: 0.5rem 1rem;
  border-radius: var(--border-radius-md);
  transition: all var(--transition-fast) ease-in-out;
  
  &:hover {
    background-color: var(--gray-100);
  }
`;

const UserAvatar = styled.div`
  width: 32px;
  height: 32px;
  border-radius: 50%;
  background: linear-gradient(135deg, var(--primary-color), var(--secondary-color));
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-weight: 600;
  font-size: 0.875rem;
`;

const UserName = styled.span`
  color: var(--gray-700);
  font-weight: 500;
  font-size: 0.875rem;
  
  @media (max-width: 768px) {
    display: none;
  }
`;

const UserDropdown = styled.div`
  position: absolute;
  top: 100%;
  right: 0;
  background-color: var(--white);
  box-shadow: var(--box-shadow-lg);
  border-radius: var(--border-radius-md);
  padding: 0.5rem 0;
  min-width: 180px;
  z-index: 1001;
  display: ${props => props.isOpen ? 'block' : 'none'};
  margin-top: 0.5rem;
`;

const UserDropdownItem = styled.button`
  display: block;
  width: 100%;
  text-align: left;
  color: var(--gray-700);
  background: none;
  border: none;
  padding: 0.75rem 1.5rem;
  transition: all var(--transition-fast) ease-in-out;
  font-weight: 500;
  font-size: 0.875rem;
  cursor: pointer;
  
  &:hover {
    color: var(--primary-color);
    background-color: var(--gray-100);
  }
`;

const UserDropdownLink = styled.a`
  display: block;
  color: var(--gray-700);
  text-decoration: none;
  padding: 0.75rem 1.5rem;
  transition: all var(--transition-fast) ease-in-out;
  font-weight: 500;
  font-size: 0.875rem;
  
  &:hover {
    color: var(--primary-color);
    background-color: var(--gray-100);
  }
`;

const Divider = styled.div`
  height: 1px;
  background-color: var(--gray-200);
  margin: 0.5rem 0;
`;

const MobileMenuButton = styled.button`
  display: none;
  background: none;
  border: none;
  color: var(--gray-700);
  font-size: 1.5rem;
  cursor: pointer;
  
  @media (max-width: 768px) {
    display: block;
  }
`;

const MobileMenu = styled.div`
  position: fixed;
  top: 70px;
  left: 0;
  right: 0;
  background-color: var(--white);
  box-shadow: var(--box-shadow-lg);
  padding: 1rem;
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  
  @media (min-width: 769px) {
    display: none;
  }
`;

const MobileNavLink = styled(Link)`
  color: var(--gray-700);
  text-decoration: none;
  padding: 0.75rem 1rem;
  border-radius: var(--border-radius-md);
  font-weight: 500;
  transition: all var(--transition-fast) ease-in-out;
  
  &:hover {
    color: var(--primary-color);
    background-color: var(--gray-100);
  }
`;

const Header = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [solutionsDropdownOpen, setSolutionsDropdownOpen] = useState(false);
  const [productsDropdownOpen, setProductsDropdownOpen] = useState(false);
  const [user, setUser] = useState(null);
  const [userDropdownOpen, setUserDropdownOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  
  // 点击外部区域关闭用户菜单
  useEffect(() => {
    const handleClickOutside = (event) => {
      const userMenu = document.querySelector('.user-menu');
      if (userMenu && !userMenu.contains(event.target)) {
        setUserDropdownOpen(false);
      }
    };
    
    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, []);

  useEffect(() => {
    const checkAuth = () => {
      const token = localStorage.getItem('token');
      const userData = localStorage.getItem('user');
      
      if (token && userData) {
        try {
          const parsedUser = JSON.parse(userData);
          setUser(parsedUser);
        } catch (error) {
          console.error('解析用户数据失败:', error);
          localStorage.removeItem('token');
          localStorage.removeItem('user');
        }
      } else {
        setUser(null);
      }
    };
    
    checkAuth();
    
    // 监听storage变化（同一浏览器不同标签页之间同步登录状态）
    const handleStorageChange = () => {
      checkAuth();
    };
    
    // 监听登录成功事件
    const handleUserLoggedIn = () => {
      checkAuth();
    };
    
    // 监听退出登录事件
    const handleUserLoggedOut = () => {
      setUser(null);
    };
    
    window.addEventListener('storage', handleStorageChange);
    window.addEventListener('userLoggedIn', handleUserLoggedIn);
    window.addEventListener('userLoggedOut', handleUserLoggedOut);
    
    return () => {
      window.removeEventListener('storage', handleStorageChange);
      window.removeEventListener('userLoggedIn', handleUserLoggedIn);
      window.removeEventListener('userLoggedOut', handleUserLoggedOut);
    };
  }, []);

  const handleLogout = async () => {
    try {
      const token = localStorage.getItem('token');
      if (token) {
        await axios.post('/api/users/logout', {}, {
          headers: { Authorization: `Bearer ${token}` }
        });
      }
    } catch (error) {
      console.error('登出失败:', error);
    } finally {
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      setUser(null);
      setUserDropdownOpen(false);
      navigate('/');
    }
  };

  const getUserDisplayName = () => {
    if (!user) return '';
    if (user.nickname) return user.nickname;
    if (user.phone) return user.phone.replace(/(\d{3})\d{4}(\d{4})/, '$1****$2');
    return '用户';
  };

  const getUserInitial = () => {
    if (!user) return '?';
    if (user.nickname) return user.nickname.charAt(0).toUpperCase();
    if (user.phone) return user.phone.charAt(0);
    return 'U';
  };

  return (
    <HeaderContainer>
      <Navbar>
        <Logo to="/">
          <img src="/images/logo.png" alt="航泰科技" />
        </Logo>
        
        <NavLinks>
          <NavLink to="/" className={location.pathname === '/' ? "active" : ""}>
            首页
          </NavLink>
          
          <NavItem 
            className={location.pathname.startsWith('/solutions') ? "active" : ""}
            onMouseEnter={() => setSolutionsDropdownOpen(true)}
            onMouseLeave={() => setSolutionsDropdownOpen(false)}
          >
            解决方案
            <SolutionsDropdown isOpen={solutionsDropdownOpen} 
              onMouseEnter={() => setSolutionsDropdownOpen(true)}
              onMouseLeave={() => setSolutionsDropdownOpen(false)}>
              <DropdownItem 
                to="/solutions?category=通用"
                onClick={() => setSolutionsDropdownOpen(false)}
              >
                通用解决方案
              </DropdownItem>
              <DropdownItem 
                to="/solutions?category=行业"
                onClick={() => setSolutionsDropdownOpen(false)}
              >
                行业解决方案
              </DropdownItem>
            </SolutionsDropdown>
          </NavItem>
          
          <NavItem 
            className={location.pathname.startsWith('/products') ? "active" : ""}
            onMouseEnter={() => setProductsDropdownOpen(true)}
            onMouseLeave={() => setProductsDropdownOpen(false)}
          >
            产品中心
            <Dropdown isOpen={productsDropdownOpen}
              onMouseEnter={() => setProductsDropdownOpen(true)}
              onMouseLeave={() => setProductsDropdownOpen(false)}>
              <DropdownItem to="/products?category=软件" onClick={() => setProductsDropdownOpen(false)}>
                软件产品
              </DropdownItem>
              <DropdownItem to="/products?category=硬件设备" onClick={() => setProductsDropdownOpen(false)}>
                硬件设备
              </DropdownItem>
            </Dropdown>
          </NavItem>
          
          <NavLink to="/service-support" className={location.pathname === '/service-support' ? "active" : ""}>
            服务支持
          </NavLink>
          
          <NavLink to="/downloads" className={location.pathname === '/downloads' ? "active" : ""}>
            文档下载
          </NavLink>
          
          <NavLink to="/contact" className={location.pathname === '/contact' ? "active" : ""}>
            关于我们
          </NavLink>
        </NavLinks>
        
        <AuthLinks>
          {user ? (
            <UserMenu 
              className="user-menu"
              onClick={(e) => {
                e.stopPropagation();
                setUserDropdownOpen(!userDropdownOpen);
              }}
            >
              {user?.avatar ? (
                <img 
                  src={user.avatar} 
                  alt="头像" 
                  style={{ 
                    width: '32px', 
                    height: '32px', 
                    borderRadius: '50%',
                    objectFit: 'cover'
                  }} 
                />
              ) : (
                <UserAvatar>{getUserInitial()}</UserAvatar>
              )}
              <UserName>{getUserDisplayName()}</UserName>
              <UserDropdown isOpen={userDropdownOpen}
                onClick={(e) => e.stopPropagation()}
              >
                <UserDropdownLink href="/profile" onClick={() => setUserDropdownOpen(false)}>
                  👤 个人中心
                </UserDropdownLink>
                <UserDropdownLink href="/profile?tab=demands" onClick={() => setUserDropdownOpen(false)}>
                  📝 需求管理
                </UserDropdownLink>
                <UserDropdownLink href="/profile?tab=announcements" onClick={() => setUserDropdownOpen(false)}>
                  📢 公告栏
                </UserDropdownLink>
                <Divider />
                <UserDropdownItem onClick={handleLogout}>
                  🚪 退出登录
                </UserDropdownItem>
              </UserDropdown>
            </UserMenu>
          ) : (
            <>
              <LoginButton to="/login">登录</LoginButton>
              <SignupButton to="/register">注册</SignupButton>
            </>
          )}
        </AuthLinks>
        
        <MobileMenuButton onClick={() => setMobileMenuOpen(!mobileMenuOpen)}>
          {mobileMenuOpen ? '✕' : '☰'}
        </MobileMenuButton>
      </Navbar>
      
      {mobileMenuOpen && (
        <MobileMenu>
          <MobileNavLink to="/" onClick={() => setMobileMenuOpen(false)}>
            首页
          </MobileNavLink>
          <MobileNavLink to="/solutions" onClick={() => setMobileMenuOpen(false)}>
            解决方案
          </MobileNavLink>
          <MobileNavLink to="/products" onClick={() => setMobileMenuOpen(false)}>
            产品中心
          </MobileNavLink>
          <MobileNavLink to="/service-support" onClick={() => setMobileMenuOpen(false)}>
            服务支持
          </MobileNavLink>
          <MobileNavLink to="/downloads" onClick={() => setMobileMenuOpen(false)}>
            文档下载
          </MobileNavLink>
          <MobileNavLink to="/contact" onClick={() => setMobileMenuOpen(false)}>
            关于我们
          </MobileNavLink>
          {user ? (
            <>
              <div style={{ 
                padding: '0.75rem 1rem', 
                borderTop: '1px solid var(--gray-200)',
                marginTop: '0.5rem'
              }}>
                <div style={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  gap: '0.5rem',
                  marginBottom: '0.5rem'
                }}>
                  {user?.avatar ? (
                    <img 
                      src={user.avatar} 
                      alt="头像" 
                      style={{ 
                        width: '32px', 
                        height: '32px', 
                        borderRadius: '50%',
                        objectFit: 'cover'
                      }} 
                    />
                  ) : (
                    <UserAvatar>{getUserInitial()}</UserAvatar>
                  )}
                  <UserName>{getUserDisplayName()}</UserName>
                </div>
              </div>
              <MobileNavLink to="/profile" onClick={() => setMobileMenuOpen(false)}>
                👤 个人中心
              </MobileNavLink>
              <MobileNavLink to="/profile?tab=demands" onClick={() => setMobileMenuOpen(false)}>
                📝 需求管理
              </MobileNavLink>
              <MobileNavLink to="/profile?tab=announcements" onClick={() => setMobileMenuOpen(false)}>
                📢 公告栏
              </MobileNavLink>
              <MobileNavLink 
                to="/" 
                onClick={(e) => {
                  e.preventDefault();
                  setMobileMenuOpen(false);
                  handleLogout();
                }}
              >
                🚪 退出登录
              </MobileNavLink>
            </>
          ) : (
            <>
              <MobileNavLink to="/login" onClick={() => setMobileMenuOpen(false)}>
                登录
              </MobileNavLink>
              <MobileNavLink to="/register" onClick={() => setMobileMenuOpen(false)}>
                注册
              </MobileNavLink>
            </>
          )}
        </MobileMenu>
      )}
    </HeaderContainer>
  );
};

export default Header;